import React from "react";
import { Box, Typography, Paper, Button, IconButton, Collapse } from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { QuizStyledCard } from "../../shared/QuizStyledCard";
import { COLORS } from "../constants";
import { formatDate } from "../helpers";
import { SubscriptionRow } from "./SubscriptionRow";
import type { AddressGroup, SubscriptionData, ModalType, SubIssues } from "../types";

interface Props {
  group: AddressGroup;
  expanded: boolean;
  onToggle: () => void;
  onOpenDeliveryModal: (addressId: string) => void;
  onEditAddress?: (addressId: string) => void;
  onOpenModal: (type: ModalType, sub: SubscriptionData) => void;
  getVariantInfo: (variantId: string) => any;
  getSubIssues: (subId: string) => SubIssues | undefined;
}

export function AddressGroupCard({ group, expanded, onToggle, onOpenDeliveryModal, onEditAddress, onOpenModal, getVariantInfo, getSubIssues }: Props) {
  return (
    <QuizStyledCard>
      <Box
        sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", cursor: "pointer", gap: 2 }}
        onClick={onToggle}
      >
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <LocationOnIcon sx={{ color: COLORS.brown, fontSize: 20 }} />
            <Typography sx={{ fontWeight: 700, fontSize: 16, color: COLORS.brownDeep }}>
              {group.address ? `${group.address.address1}, ${group.address.city}` : `Address ID: ${group.addressId}`}
            </Typography>
            {onEditAddress && (
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); onEditAddress(group.addressId); }}
                sx={{ color: COLORS.brown }}
              >
                <EditOutlinedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}
          </Box>
          {group.address && (
            <Typography sx={{ fontSize: 12, color: COLORS.textMuted, ml: 3.5 }}>
              {group.address.first_name} {group.address.last_name}
              {group.address.address2 && `, ${group.address.address2}`}
              , {group.address.province} {group.address.zip}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box sx={{ textAlign: "right" }}>
            <Typography sx={{ fontSize: 12, color: COLORS.textMuted }}>Subscriptions</Typography>
            <Typography sx={{ fontWeight: 700, color: COLORS.brown }}>{group.allSubs.length}</Typography>
          </Box>
          <IconButton size="small">
            {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </Box>
      </Box>

      <Paper
        sx={{
          mt: 2, p: 2, bgcolor: COLORS.blueLight, borderRadius: "12px",
          display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2,
        }}
      >
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <CalendarTodayIcon sx={{ color: COLORS.blue, fontSize: 18 }} />
            <Typography sx={{ fontWeight: 700, fontSize: 14, color: COLORS.blue }}>Next Delivery</Typography>
          </Box>
          <Typography sx={{ fontWeight: 800, fontSize: 18, color: COLORS.blueDark }}>
            {formatDate(group.deliveryDate)}
          </Typography>
          <Typography sx={{ fontSize: 11, color: COLORS.blue, mt: 0.5 }}>
            Ship: {formatDate(group.shipDate)} &bull; Charge: {formatDate(group.chargeDate)}
          </Typography>
        </Box>

        <Button
          variant="contained"
          startIcon={<EditOutlinedIcon />}
          onClick={(e) => { e.stopPropagation(); onOpenDeliveryModal(group.addressId); }}
          sx={{ bgcolor: COLORS.blue, "&:hover": { bgcolor: COLORS.blueDark }, textTransform: "none", fontWeight: 600 }}
        >
          Change Date
        </Button>
      </Paper>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ mt: 3 }}>
          {group.bundleSubs.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography sx={{ fontWeight: 700, fontSize: 14, color: COLORS.brown, mb: 1.5 }}>
                Meal Plans ({group.bundleSubs.length})
              </Typography>
              {group.bundleSubs.map(sub => (
                <SubscriptionRow
                  key={sub.id}
                  sub={sub}
                  isBundle
                  subIssues={getSubIssues(sub.id)}
                  getVariantInfo={getVariantInfo}
                  onOpenModal={onOpenModal}
                />
              ))}
            </Box>
          )}
          {group.treatSubs.length > 0 && (
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: 14, color: COLORS.brown, mb: 1.5 }}>
                Treats ({group.treatSubs.length})
              </Typography>
              {group.treatSubs.map(sub => (
                <SubscriptionRow
                  key={sub.id}
                  sub={sub}
                  isBundle={false}
                  isTreat
                  subIssues={getSubIssues(sub.id)}
                  getVariantInfo={getVariantInfo}
                  onOpenModal={onOpenModal}
                />
              ))}
            </Box>
          )}
        </Box>
      </Collapse>
    </QuizStyledCard>
  );
}
